
import Vue from "vue";
import { mapState } from "vuex";

import {
  required,
  requiredIf,
  maxLength,
  minLength
} from "vuelidate/lib/validators";
import AirpointsIcon from "@/assets/icons/airpoints.svg";
import AirpointsWhiteIcon from "@/assets/icons/airpoints-white.svg";
import AirpointsMobileIcon from "@/assets/icons/airpoints-mobile.svg";
import AirpointsMobileWhiteIcon from "@/assets/icons/airpoints-mobile-white.svg";
import FlybuysIcon from "@/assets/icons/flybuys.svg";
import FlybuysMobileIcon from "@/assets/icons/flybuys-mobile.svg";
import FlybuysMobileWhiteIcon from "@/assets/icons/flybuys-mobile-white.svg";
import { LOYALTY } from "@/store/loyalty";
import { LoyaltySchemeType } from "@/store/loyalty/types";
import { IRootState } from "@/store/types";
import { OfferLevelType } from "@/store/loyalty/types";

const FIELD_LOYALTY_TYPE_AIRPOINTS = "airpointsField";
const FIELD_LOYALTY_TYPE_FLYBUYS = "flyBuysField";

export default Vue.extend({
  props: {
    pagination: Object,
    dialogType: String
  },
  components: {
    AirpointsIcon,
    AirpointsMobileIcon,
    AirpointsWhiteIcon,
    AirpointsMobileWhiteIcon,
    FlybuysIcon,
    FlybuysMobileIcon,
    FlybuysMobileWhiteIcon
  },
  computed: {
    noteMessage() {
      switch (this.dialogType) {
        case OfferLevelType.Card:
          return "this card";
          break;
        case OfferLevelType.CostCentre:
          return "all cards under this cost centre";
          break;
        default:
          return "all cards under this account";
      }
    },
    loyaltyNumber: {
      get() {
        return this.activeCustomerCard && this.activeCustomerCard.loyaltyNumber;
      },
      set(value: any) {
        this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.SET_ACTIVE_CUSTOMER_CARD),
          { ...this.activeCustomerCard, loyaltyNumber: value }
        );
      }
    },
    loyaltyScheme: {
      get() {
        return this.activeCustomerCard && this.activeCustomerCard.loyaltyScheme;
      },
      set(value: any) {
        this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.SET_ACTIVE_CUSTOMER_CARD),
          { ...this.activeCustomerCard, loyaltyScheme: value }
        );
      }
    },
    isDisabled() {
      const { isValidating, apiIsValid } = this;
      return this.$v.$invalid || isValidating || apiIsValid;
    },
    accountNumber() {
      return this.activeCustomerCard && this.activeCustomerCard.accountNumber;
    },
    cardNumber() {
      return this.activeCustomerCard && this.activeCustomerCard.cardNumber;
    },
    costCentreCode() {
      return this.activeCustomerCard && this.activeCustomerCard.costCentreCode;
    },
    flyBuysErrorMessages() {
      const { $v } = this;
      const { isValidating, apiIsValid } = this;

      let errors: string[] = [];

      if (isValidating || !$v.loyaltyNumber.$dirty) {
        return [];
      }

      if (!$v.loyaltyNumber.required) {
        errors = [...errors, "This field is required"];
      }

      // dont show error message when apiIsValid is null or true
      if (!$v.loyaltyNumber.minLength || apiIsValid === false) {
        errors = [...errors, "This field is invalid"];
      }

      return errors;
    },
    ...mapState({
      activeCustomerCard: (state: IRootState) =>
        state.loyalty.activeCustomerCard
    })
  },
  data() {
    return {
      LoyaltySchemeType: LoyaltySchemeType,
      inputIsValid: false,
      isValidating: false,
      apiIsValid: null,
      airpointsFirstName: null,
      airpointsLastName: null
    };
  },
  methods: {
    getAirpointsValidaitonErrors(key: string) {
      const { $v } = this;
      const { isValidating, apiIsValid } = this;

      let errors: string[] = [];

      if (
        isValidating ||
        ($v[key] && (!$v[key].$dirty && apiIsValid !== false))
      ) {
        return [];
      }

      if ($v[key] && !$v[key].required) {
        errors = [...errors, "This field is required"];
      }

      // dont show error message when apiIsValid is null or true
      // when apiIsValid = null it means the api validation hasn't been performed
      if (apiIsValid === false) {
        errors = [...errors, "This field is invalid"];
      }

      return errors;
    },
    onLoyaltySchemeChange(value: any) {
      this.resetLoyaltyScheme();
      this.loyaltyScheme = value;
    },
    resetApiIsValid() {
      this.apiIsValid = null;
    },
    async onApply() {
      this.isValidating = true;
      let result;

      if (this.dialogType === OfferLevelType.Card) {
        result = await this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_CARD_LOYALTY_ASSOCIATION),
          {
            accountNumber: this.accountNumber,
            cardNumber: this.cardNumber,
            loyaltyNumber: this.loyaltyNumber,
            loyaltyScheme: this.loyaltyScheme,
            airpointsFirstName: this.airpointsFirstName,
            airpointsLastName: this.airpointsLastName
          }
        );
      } else if (this.dialogType === OfferLevelType.Account) {
        result = await this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_ACCOUNT_LOYALTY_ASSOCIATION),
          {
            accountNumber: this.accountNumber,
            loyaltyNumber: this.loyaltyNumber,
            loyaltyScheme: this.loyaltyScheme,
            airpointsFirstName: this.airpointsFirstName,
            airpointsLastName: this.airpointsLastName
          }
        );
      } else if (this.dialogType === OfferLevelType.CostCentre) {
        result = await this.$store.dispatch(
          LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_COST_CENTRE_LOYALTY_ASSOCIATION),
          {
            accountNumber: this.accountNumber,
            costCentreCode: this.costCentreCode,
            loyaltyNumber: this.loyaltyNumber,
            loyaltyScheme: this.loyaltyScheme,
            airpointsFirstName: this.airpointsFirstName,
            airpointsLastName: this.airpointsLastName
          }
        );
      }
      this.apiIsValid = !result.validationFailed;
      this.isValidating = false;
      if (result.success) {
        // reload card details
        this.$store.dispatch(LOYALTY.NS(LOYALTY.ACTIONS.LOAD_CUSTOMER_CARDS), {
          customerId: this.accountNumber
        });

        // close modal
        this.onCancel();
      }
    },
    onCancel() {
      this.resetLoyaltyScheme();
      this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.SET_ACTIVE_CUSTOMER_CARD),
        undefined
      );
    },
    onChangeLoyaltyNumber(value: any) {
      this.loyaltyNumber = value;
      this.resetApiIsValid();
    },
    onChangeAirpointsFirstName(value: any) {
      this.airpointsFirstName = value;
      this.resetApiIsValid();
    },
    onChangeAirpointsLastName(value: any) {
      this.airpointsLastName = value;
      this.resetApiIsValid();
    },
    resetLoyaltyScheme() {
      this.loyaltyScheme = null;
      this.loyaltyNumber = "";
      this.airpointsFirstName = "";
      this.airpointsLastName = "";
      this.$v.$reset();
      this.resetApiIsValid();
    }
  },
  validations() {
    return {
      loyaltyScheme: {
        required
      },
      loyaltyNumber: {
        required,
        minLength:
          this.loyaltyScheme === LoyaltySchemeType.FlyBuys
            ? minLength(16)
            : minLength(1)
      },
      airpointsFirstName: {
        required: requiredIf(
          nestedModel =>
            nestedModel.loyaltyScheme === LoyaltySchemeType.Airpoints
        )
      },
      airpointsLastName: {
        required: requiredIf(
          nestedModel =>
            nestedModel.loyaltyScheme === LoyaltySchemeType.Airpoints
        )
      }
    };
  }
});
